// Pure severity / category metadata — no React, no Leaflet, no DOM. This split
// exists so the report generator (report-doc.ts) can run inside the console
// Worker: meta.tsx pulls React and (via geo.ts) Leaflet, which touches `window`
// at module load and cannot be bundled into workerd. Everything UI-flavoured
// stays in meta.tsx, which re-exports these for existing call sites.
import type { DefectCategory, Severity } from "./types";

// ── Severity ──────────────────────────────────────────────────────────
export interface SeverityMeta { label: string; color: string; bg: string; bc: string; rank: number }
// Canonical severity palette (report + viewers must stay consistent):
// minor grey, moderate BRAND ORANGE, major red.
export const SEVERITY: Record<Severity, SeverityMeta> = {
  minor: { label: "Minor", color: "#7E8896", bg: "rgba(126,136,150,0.14)", bc: "rgba(126,136,150,0.30)", rank: 1 },
  moderate: { label: "Moderate", color: "#F97316", bg: "rgba(249,115,22,0.12)", bc: "rgba(249,115,22,0.32)", rank: 2 },
  major: { label: "Major", color: "#EF4444", bg: "rgba(239,68,68,0.13)", bc: "rgba(239,68,68,0.34)", rank: 3 },
};
export const SEV_ORDER: Severity[] = ["major", "moderate", "minor"];
export const sevMeta = (sev: string): SeverityMeta => SEVERITY[sev as Severity] ?? SEVERITY.minor;

// ── Defect type / category ────────────────────────────────────────────
// Pipeline-native taxonomy (§2 #6 rev. v0.3). Chips stay neutral by design:
// severity carries the colour, type stays quiet.
export const CATEGORY: Record<DefectCategory, { label: string }> = {
  corrosion: { label: "Corrosion" },
  coating_failure: { label: "Coating failure" },
  ponding: { label: "Ponding" },
  staining: { label: "Staining / water marks" },
  biological_growth: { label: "Biological growth" },
  vegetation: { label: "Vegetation" },
  crack: { label: "Cracking" },
  spall: { label: "Spalling" },
  deformation: { label: "Deformation" },
  damaged_cladding: { label: "Damaged cladding" },
  damaged_flashing: { label: "Damaged flashing" },
  sealant_failure: { label: "Sealant failure" },
  fasteners: { label: "Fasteners" },
  glazing_damage: { label: "Glazing damage" },
  debris: { label: "Debris" },
  abandoned_material: { label: "Abandoned material" },
  equipment_deterioration: { label: "Equipment deterioration" },
  signage_damage: { label: "Signage damage" },
  other: { label: "Other" },
};

// Display families: presentational grouping (filters, form picker) and the
// remediation suggestion engine's category families — never stored.
export const CAT_FAMILIES: { id: string; label: string; cats: DefectCategory[] }[] = [
  { id: "corrosion", label: "Corrosion & coatings", cats: ["corrosion", "coating_failure"] },
  { id: "water", label: "Water & drainage", cats: ["ponding", "staining"] },
  { id: "biological", label: "Biological", cats: ["biological_growth", "vegetation"] },
  { id: "structural", label: "Structural", cats: ["crack", "spall", "deformation"] },
  { id: "envelope", label: "Envelope", cats: ["damaged_cladding", "damaged_flashing", "sealant_failure", "fasteners", "glazing_damage"] },
  { id: "housekeeping", label: "Housekeeping", cats: ["debris", "abandoned_material"] },
  { id: "equipment", label: "Equipment & signage", cats: ["equipment_deterioration", "signage_damage"] },
  { id: "other", label: "Other", cats: ["other"] },
];
export const CAT_ORDER: DefectCategory[] = CAT_FAMILIES.flatMap((f) => f.cats);
export const catOf = (d: { category?: string } | null | undefined): DefectCategory =>
  d && d.category && d.category in CATEGORY ? (d.category as DefectCategory) : "other";
export const catLabel = (c: string): string => (CATEGORY[c as DefectCategory] ?? CATEGORY.other).label;
